import { Fragment, type ReactNode } from "react";

/**
 * `{token}` substitution that yields React nodes instead of a string.
 *
 * `auth.tos` reads "I agree to the {tosLink} and {privacyLink}." and both
 * placeholders have to come out as *tappable links*, not as text. String
 * concatenation would get the words in the wrong order in any language that
 * puts them there, and `dangerouslySetInnerHTML` would put copy from the
 * catalogue into the HTML parser — so the template is split on the tokens and
 * the caller supplies the nodes.
 *
 * Deliberately tiny and deliberately not a `Trans` component: `@wr/i18n` is a
 * catalogue plus `t()`, and this is the one place a string needs structure.
 */
export function renderTemplate(template: string, nodes: Record<string, ReactNode>): ReactNode {
  const parts = template.split(/(\{\w+\})/g);

  return parts.map((part, index) => {
    const match = /^\{(\w+)\}$/.exec(part);
    const node = match?.[1] === undefined ? undefined : nodes[match[1]];
    // An unmatched placeholder stays visible verbatim, same rule as
    // `interpolate()`: a `{tosLink}` on screen is a bug report, an empty gap
    // is not.
    return <Fragment key={index}>{node ?? part}</Fragment>;
  });
}
